import React, { createContext, useState, useEffect, useContext } from 'react';
import { UserContext } from './userContext';
import * as endPoints from '../js/endpoints';

// Define available message types and their default colors
export const MESSAGE_TYPES = {
  success:'rgba(56, 160, 78, 0.66)',
  error: 'rgba(201, 59, 73, 0.68)',
  warning: 'rgba(215, 176, 60, 0.68)',
  info: 'rgba(100, 124, 128, 0.68)',
};

// Define icons for each message type (FontAwesome classes)
const ICON_TYPES = {
  success: 'fas fa-check-circle',
  error:   'fas fa-exclamation-circle',
  warning: 'fas fa-exclamation-triangle',
  info:    'fas fa-info-circle',
};

// Context holds current notification and functions to show/clear it
const NotificationContext = createContext({
  message: '',
  type: 'info',
  color: MESSAGE_TYPES.info,
  showNotification: (msg, type, color) => {},
  clearNotification: () => {},
  reminderConfig: { enabled: false, daysBefore: 3 },
  updateReminderConfig: cfg => {},
});

export function NotificationProvider({ children }) {
  const [notification, setNotification] = useState({
    message: '',
    type: 'info',
    color: MESSAGE_TYPES.info,
  });
  const { user } = useContext(UserContext);

  const [reminderConfig, setReminderConfig] = useState(() => {
    const saved = localStorage.getItem('reminderConfig');
    return saved ? JSON.parse(saved) : { enabled: false, daysBefore: 3 };
  });

  const [notifiedIds, setNotifiedIds] = useState(() => {
    const saved = localStorage.getItem('notifiedAppointments');
    return saved ? JSON.parse(saved) : [];
  });

  // Auto-clear after 3 seconds
  useEffect(() => {
    const { message } = notification;
    if (message) {
      const timer = setTimeout(() => {
        setNotification({ message: '', type: 'info', color: MESSAGE_TYPES.info });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Helper to show a notification
  function showNotification(message, type = 'info', color) {
    const defaultColor = MESSAGE_TYPES[type] || MESSAGE_TYPES.info;
    setNotification({ message, type, color: color || defaultColor });
  }

  const updateReminderConfig = cfg => {
    setReminderConfig(prev => {
      const newCfg = { ...prev, ...cfg };
      localStorage.setItem('reminderConfig', JSON.stringify(newCfg));
      return newCfg;
    });
  };

  const saveNotified = ids => {
    setNotifiedIds(ids);
    localStorage.setItem('notifiedAppointments', JSON.stringify(ids));
  };

  const checkAppointments = async () => {
    if (!user.isLogged || !reminderConfig.enabled) return;
    try {
      const data = await endPoints.getAppointmentsByRole(user.role, user.id);
      const now = new Date();
      const limit = reminderConfig.daysBefore * 24 * 60 * 60 * 1000;
      const upcoming = (Array.isArray(data) ? data : []).filter(a => {
        const dt = new Date(`${a.date}T${a.time || '00:00'}`);
        return dt - now > 0 && dt - now <= limit;
      });
      const newIds = [...notifiedIds];
      upcoming.forEach(a => {
        if (!newIds.includes(a.id)) {
          showNotification(
            `Cita con ${(a.client_name && a.client_name !== 'null') ? a.client_name : 'Sin cliente'} - ${a.service_name} el ${a.date} a las ${a.time}`,
            'info'
          );
          newIds.push(a.id);
        }
      });
      if (newIds.length !== notifiedIds.length) saveNotified(newIds);
    } catch (_err) {
    }
  };

  // Helper to clear notification
  function clearNotification() {
    setNotification({ message: '', type: 'info', color: MESSAGE_TYPES.info });
  }

  useEffect(() => {
    checkAppointments();
    const interval = setInterval(checkAppointments, 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user.id, user.role, reminderConfig]);

  return (
    <NotificationContext.Provider value={{
      ...notification,
      showNotification,
      clearNotification,
      reminderConfig,
      updateReminderConfig,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

// Hook to use notification context in components
export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}

// Notification display component
export function Notification() {
  const { message, type, color, clearNotification } = useNotification();

  if (!message) return null;

  // Container style: top center
  const containerStyle = {
    position: 'fixed',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: color,
    color: '#fff',
    padding: '1rem 1.5rem',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    minWidth: '300px',
  };

  const iconStyle = {
    marginRight: '0.75rem',
    fontSize: '1.2rem',
  };

  const messageStyle = {
    flex: 1,
  };

  const closeStyle = {
    marginLeft: '0.75rem',
    cursor: 'pointer',
    fontSize: '1.2rem',
  };

  return (
    <div style={containerStyle}>
      <i className={ICON_TYPES[type] || ICON_TYPES.info} style={iconStyle} />
      <div style={messageStyle}>{message}</div>
      <i className="fas fa-times" style={closeStyle} onClick={clearNotification} />
    </div>
  );
}
